import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { setFeedOrder } from "web/redux/ducks/feed-filters";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { SortOptionSerializer } from "web/types/serializers";
import * as styles from "./feed-sort.css";

const SortByData = {
    newest: {
        analyticsLabel: "SORT_BY_NEWEST",
        label: gettext("feed.filters.newest"), // "Newest"
    },
    price_desc: {
        analyticsLabel: "SORT_BY_HIGH_LOW",
        label: gettext("feed.filters.price_high_low"), // "Price (high to low)"
    },
    price_asc: {
        analyticsLabel: "SORT_BY_LOW_HIGH",
        label: gettext("feed.filters.price_low_high"), // "Price (low to high)"
    },
    recommended: {
        analyticsLabel: "SORT_BY_RECOMMENDED",
        label: gettext("feed.filters.recommended"), // "Recommended"
    },
    discount_desc: {
        analyticsLabel: "SORT_BY_DISCOUNT_HIGH_LOW",
        label: gettext("feed.filters.discount_high_low"), // "Discount (high to low)"
    },
};

export interface FeedSortProps {
    sortOptions: SortOptionSerializer[];
    showSelected?: boolean;
}

export function FeedSort({ sortOptions, showSelected = false }: FeedSortProps): JSX.Element {
    const { isDomLoaded, isDesktopViewport } = useDomViewport();

    const sort = useSelector((state: ReduxStoreState) => state.feedFiltersReducer.view);
    const dispatch = useDispatch();

    const selected = useMemo(() => {
        for (let option of sortOptions) {
            if (typeof sort === "undefined" && option.selected) {
                return option;
            } else if (option.value === sort) {
                return option;
            }
        }
        // Fallback for when bad things happen
        return sortOptions[0];
    }, [sort, sortOptions]);

    const [label, setLabel] = useState(SortByData[selected.value].label);

    function onChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        const sort = event.target.value;
        const { analyticsLabel, label } = SortByData[sort];
        analytics.event("sort_by", analyticsLabel);
        dispatch(setFeedOrder(sort));
        setLabel(label);
    }

    const testRef = useRef(null);

    return (
        <div className={styles.wrapper}>
            <Text textStyle={"body-3"}>
                {gettext("feed.filters.sort_by")}
                {(showSelected || isDesktopViewport) && (
                    <>
                        <Text as="span" textStyle={"body-3"} className={styles.mobile}>
                            {": "}
                        </Text>
                        <Text as="span" textStyle={"body-3"} className={styles.filterText}>
                            {label}
                        </Text>
                    </>
                )}
            </Text>
            <div className={styles.select}>
                <select
                    aria-label={gettext("feed.sort.mobile_btn_text")}
                    value={selected.value}
                    onChange={onChange}
                    ref={testRef}
                >
                    {sortOptions.map(({ value }) => (
                        // TODO if the experiment is successful fix Update the sortOptions serializer
                        // As we want to move translations on the FE
                        <option key={value} value={value}>
                            {isDomLoaded && SortByData[value].label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}
